import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TextHuntResults({ user }) {
  const { texthuntId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [texthunt, setTexthunt] = useState(null);
  const [results, setResults] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showClassroomModal, setShowClassroomModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedCoursework, setSelectedCoursework] = useState('');
  const [availableCourseworks, setAvailableCourseworks] = useState([]);
  const [loadingCourseworks, setLoadingCourseworks] = useState(false);
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [submittingGrades, setSubmittingGrades] = useState(false);
  const [evaluatingWithAI, setEvaluatingWithAI] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [showRegradeModal, setShowRegradeModal] = useState(false);
  const [newStrictness, setNewStrictness] = useState('normal');
  const [regrading, setRegrading] = useState(false);
  const [showEditScoreModal, setShowEditScoreModal] = useState(false);
  const [editingScore, setEditingScore] = useState(null);
  const [newScore, setNewScore] = useState('');
  const [scoreReason, setScoreReason] = useState('');

  useEffect(() => {
    loadData();
    loadCourses();
  }, [texthuntId]);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadCourseworks = async (courseId) => {
    setLoadingCourseworks(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/course/${courseId}/courseworks/${user.id}`);
      setAvailableCourseworks(response.data.courseworks || []);
    } catch (error) {
      console.error('Error loading courseworks:', error);
      setAvailableCourseworks([]);
    } finally {
      setLoadingCourseworks(false);
    }
  };

  const loadData = async () => {
    try {
      const [texthuntRes, resultsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/texthunt/${texthuntId}`),
        axios.get(`${BACKEND_URL}/api/texthunt/${texthuntId}/results`)
      ]);
      
      setTexthunt(texthuntRes.data);
      setResults(resultsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error al cargar los resultados');
      navigate('/teacher');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClassroomAssignment = async () => {
    if (!selectedCourse) {
      alert('Por favor selecciona una clase');
      return;
    }

    setCreatingAssignment(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teacher/texthunt/${texthuntId}/create-classroom-assignment`,
        {
          teacher_id: user.id,
          course_id: selectedCourse
        }
      );

      setSelectedCoursework(response.data.coursework_id);
      alert(`✅ Tarea creada en Classroom: ${response.data.title}`);
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Error al crear la tarea en Classroom: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingAssignment(false);
    }
  };

  const handleSubmitAllGrades = async () => {
    if (!selectedCourse || !selectedCoursework) {
      alert('Por favor crea primero la tarea en Classroom o selecciona una existente');
      return;
    }

    if (!window.confirm(`¿Enviar las ${results.length} calificaciones a Google Classroom?`)) {
      return;
    }

    setSubmittingGrades(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teacher/texthunt/${texthuntId}/submit-all-grades`,
        {
          teacher_id: user.id,
          course_id: selectedCourse,
          coursework_id: selectedCoursework
        }
      );

      alert(`✅ ${response.data.message}\n${response.data.errors.length > 0 ? 'Errores:\n' + response.data.errors.join('\n') : ''}`);
      setShowClassroomModal(false);
      loadData(); // Reload to show updated status
    } catch (error) {
      console.error('Error submitting grades:', error);
      alert('Error al enviar calificaciones: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmittingGrades(false);
    }
  };

  const handleEvaluateAllWithAI = async () => {
    if (!window.confirm('¿Evaluar todas las respuestas cortas pendientes con ChatGPT?\n\nPodrás revisar y modificar las puntuaciones después.')) {
      return;
    }

    setEvaluatingWithAI(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teacher/texthunt/${texthuntId}/evaluate-all-with-ai`,
        {}
      );

      alert(`✅ ${response.data.message}\n${response.data.errors > 0 ? `Errores: ${response.data.errors}` : ''}`);
      loadData(); // Reload to show updated scores
    } catch (error) {
      console.error('Error evaluating with AI:', error);
      alert('Error al evaluar con IA: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEvaluatingWithAI(false);
    }
  };

  const handleUpdateScore = async (resultId, questionId, newScore, newFeedback) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/teacher/texthunt/result/${resultId}/update-score`,
        {
          question_id: questionId,
          manual_score: newScore,
          manual_feedback: newFeedback
        }
      );

      alert('✅ Puntuación actualizada');
      loadData(); // Reload
      setEditingAnswer(null);
    } catch (error) {
      console.error('Error updating score:', error);
      alert('Error al actualizar puntuación: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleViewDetails = (result) => {
    setSelectedResult(result);
    setShowDetailModal(true);
  };

  // Count pending evaluations
  const countPendingEvaluations = () => {
    let count = 0;
    results.forEach(result => {
      result.answers.forEach(answer => {
        const question = texthunt.questions.find(q => q.question_id === answer.question_id);
        if (question && question.question_type === 'short_answer' && 
            answer.ai_score === null && answer.manual_score === null) {
          count++;
        }
      });
    });
    return count;
  };


  const handleEditScore = (result) => {
    setEditingScore(result);
    setNewScore(result.total_score.toString());
    setScoreReason('');
    setShowEditScoreModal(true);
  };

  const handleSaveScore = async () => {
    if (!newScore || isNaN(newScore)) {
      alert('Por favor ingresa una calificación válida');
      return;
    }

    const scoreValue = parseInt(newScore);
    if (scoreValue < 0 || scoreValue > texthunt.total_points) {
      alert(`La calificación debe estar entre 0 y ${texthunt.total_points}`);
      return;
    }

    if (!scoreReason.trim()) {
      alert('Por favor indica el motivo de la modificación');
      return;
    }

    try {
      await axios.patch(`${BACKEND_URL}/api/texthunt/result/${editingScore.id}/score`, {
        new_score: scoreValue,
        reason: scoreReason,
        modified_by: user.email
      });

      alert('Calificación actualizada correctamente');
      setShowEditScoreModal(false);
      loadData(); // Recargar datos
    } catch (error) {
      console.error('Error updating score:', error);
      alert('Error al actualizar la calificación');
    }
  };

  const handleDeleteResult = async (resultId, studentName) => {
    if (!window.confirm(`¿Eliminar el resultado de ${studentName}?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/teacher/texthunt/result/${resultId}`);
      alert('✅ Resultado eliminado');
      loadData(); // Reload
    } catch (error) {
      console.error('Error deleting result:', error);
      alert('Error al eliminar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRegrade = async () => {
    if (!window.confirm(`¿Recalificar TODAS las respuestas cortas con nivel "${newStrictness}"?\n\nEsto sobrescribirá las calificaciones actuales de IA (no las manuales).`)) {
      return;
    }

    setRegrading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teacher/texthunt/${texthuntId}/regrade-with-new-strictness`,
        { new_strictness: newStrictness }
      );

      alert(`✅ ${response.data.message}\n${response.data.errors > 0 ? `Errores: ${response.data.errors}` : ''}`);
      setShowRegradeModal(false);
      loadData(); // Reload to show updated scores
    } catch (error) {
      console.error('Error regrading:', error);
      alert('Error al recalificar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRegrading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  if (!texthunt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">TextHunt no encontrado</p>
          <button
            onClick={() => navigate('/teacher')}
            className="mt-4 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  const averageScore = results.length > 0 
    ? Math.round(results.reduce((sum, r) => sum + r.total_score, 0) / results.length)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📚 Resultados: {texthunt.title}</h1>
              <p className="text-gray-600 mt-2">{texthunt.description || 'Actividad de comprensión lectora'}</p>
            </div>
            <div className="flex gap-2">
              {results.length > 0 && countPendingEvaluations() > 0 && (
                <button
                  onClick={handleEvaluateAllWithAI}
                  disabled={evaluatingWithAI}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {evaluatingWithAI ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Evaluando...
                    </>
                  ) : (
                    <>
                      🤖 Evaluar {countPendingEvaluations()} con IA
                    </>
                  )}
                </button>
              )}
              {results.length > 0 && (
                <>
                  <button
                    onClick={() => setShowRegradeModal(true)}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                  >
                    🔄 Recalificar
                  </button>
                  <button
                    onClick={() => setShowClassroomModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"/>
                    </svg>
                    Exportar a Classroom
                  </button>
                </>
              )}
              <button
                onClick={() => navigate('/teacher')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ← Volver
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-purple-100 rounded-lg p-4">
              <div className="text-3xl font-bold text-purple-600">{results.length}</div>
              <div className="text-sm text-gray-700">Estudiantes</div>
            </div>
            <div className="bg-blue-100 rounded-lg p-4">
              <div className="text-3xl font-bold text-blue-600">{texthunt.questions.length}</div>
              <div className="text-sm text-gray-700">Preguntas</div>
            </div>
            <div className="bg-green-100 rounded-lg p-4">
              <div className="text-3xl font-bold text-green-600">{averageScore}</div>
              <div className="text-sm text-gray-700">Promedio ({texthunt.total_points} pts)</div>
            </div>
            <div className="bg-yellow-100 rounded-lg p-4">
              <div className="text-3xl font-bold text-yellow-600">
                {results.length > 0 ? Math.round((averageScore / texthunt.total_points) * 100) : 0}%
              </div>
              <div className="text-sm text-gray-700">Porcentaje</div>
            </div>
          </div>
        </div>

        {/* Results List */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Tabla de Resultados
          </h2>

          {results.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay resultados aún
              </h3>
              <p className="text-gray-500">Los estudiantes aún no han completado esta actividad</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Estudiante</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Puntuación</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Porcentaje</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Completado</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => {
                    const percentage = Math.round((result.total_score / texthunt.total_points) * 100);
                    const completedDate = new Date(result.completed_at);
                    
                    return (
                      <tr 
                        key={result.id} 
                        className={`border-b hover:bg-gray-50 ${
                          index < 3 ? 'bg-yellow-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3 font-semibold text-gray-700">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-800">{result.student_name}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className={`font-bold ${result.score_modified ? 'text-orange-600' : 'text-purple-600'}`}>
                              {result.total_score} / {texthunt.total_points}
                            </span>
                            {result.score_modified && (
                              <span 
                                className="text-orange-500 text-xs cursor-help" 
                                title={`Modificada: ${result.score_modification_reason}\nPor: ${result.score_modified_by}\nOriginal: ${result.original_score}`}
                              >
                                ⚠️
                              </span>
                            )}
                            <button
                              onClick={() => handleEditScore(result)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                              title="Editar calificación"
                            >
                              ✏️
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            percentage >= 80 ? 'bg-green-100 text-green-700' :
                            percentage >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {percentage}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {completedDate.toLocaleDateString()} {completedDate.toLocaleTimeString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => handleViewDetails(result)}
                              className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-semibold"
                            >
                              📝 Revisar
                            </button>
                            <button
                              onClick={() => navigate(`/texthunt/${texthuntId}/result/${result.student_id}`)}
                              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold"
                            >
                              👁️ Ver
                            </button>
                            <button
                              onClick={() => handleDeleteResult(result.id, result.student_name)}
                              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Question Analysis */}
        {results.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Análisis por Pregunta
            </h2>
            
            <div className="space-y-4">
              {texthunt.questions.map((question, qIndex) => {
                // Calculate success rate for this question
                let correctCount = 0;
                results.forEach(result => {
                  const answer = result.answers.find(a => a.question_id === question.question_id);
                  if (answer) {
                    if (question.question_type === 'multiple_choice' && answer.is_correct) {
                      correctCount++;
                    } else if (question.question_type === 'short_answer' && answer.ai_score >= 70) {
                      correctCount++;
                    }
                  }
                });
                
                const successRate = results.length > 0 
                  ? Math.round((correctCount / results.length) * 100) 
                  : 0;

                return (
                  <div key={question.question_id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <span className="font-semibold text-gray-700">Pregunta {qIndex + 1}: </span>
                        <span className="text-gray-800">{question.question_text}</span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ml-4 ${
                        successRate >= 70 ? 'bg-green-100 text-green-700' :
                        successRate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {successRate}% correctas
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Tipo:</span> {
                        question.question_type === 'multiple_choice' ? '☑️ Opción Múltiple' : '📝 Respuesta Corta'
                      } | 
                      <span className="ml-2 font-medium">Puntos:</span> {question.points}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail/Edit Modal */}
      {showDetailModal && selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-4">
              📝 Revisar Respuestas: {selectedResult.student_name}
            </h3>
            
            <div className="space-y-4 mb-6">
              {selectedResult.answers.map((answer, index) => {
                const question = texthunt.questions.find(q => q.question_id === answer.question_id);
                if (!question) return null;
                
                const isShortAnswer = question.question_type === 'short_answer';
                const hasScore = answer.ai_score !== null || answer.manual_score !== null;
                const finalScore = answer.manual_score !== null ? answer.manual_score : answer.ai_score;
                
                return (
                  <div 
                    key={answer.question_id}
                    className={`border-2 rounded-lg p-4 ${
                      isShortAnswer 
                        ? (hasScore 
                            ? (finalScore >= 70 ? 'border-green-300 bg-green-50' : 'border-yellow-300 bg-yellow-50')
                            : 'border-gray-300 bg-gray-50')
                        : (answer.is_correct ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50')
                    }`}
                  >
                    <div className="mb-2">
                      <span className="font-semibold">Pregunta {index + 1}: </span>
                      <span>{question.question_text}</span>
                    </div>
                    
                    {/* Multiple Choice */}
                    {question.question_type === 'multiple_choice' && (
                      <div>
                        <div className="text-sm mb-1">
                          <span className="font-semibold">Respuesta: </span>
                          <span className={answer.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>
                            {question.options[parseInt(answer.answer)]}
                            {answer.is_correct ? ' ✓' : ' ✗'}
                          </span>
                        </div>
                        {!answer.is_correct && (
                          <div className="text-sm text-green-700">
                            <span className="font-semibold">Correcta: </span>
                            {question.options[question.correct_answer_index]}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Short Answer */}
                    {isShortAnswer && (
                      <div>
                        <div className="text-sm mb-2">
                          <span className="font-semibold">Respuesta del estudiante:</span>
                          <div className="mt-1 p-2 bg-white rounded border">
                            {answer.answer}
                          </div>
                        </div>
                        
                        {question.model_answer && (
                          <div className="text-sm mb-2">
                            <span className="font-semibold">Respuesta modelo (referencia):</span>
                            <div className="mt-1 p-2 bg-blue-50 rounded border border-blue-200 text-gray-600 italic">
                              {question.model_answer}
                              <div className="text-xs mt-1">
                                (Solo referencia - ChatGPT evalúa con criterio propio)
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* AI Evaluation */}
                        {answer.ai_score !== null && (
                          <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-semibold">🤖 Evaluación IA:</span>
                              <span className={`px-2 py-1 rounded text-sm font-bold ${
                                answer.ai_score >= 70 ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                              }`}>
                                {answer.ai_score}/100
                              </span>
                            </div>
                            {answer.ai_feedback && (
                              <div className="text-sm mt-1 text-gray-700">{answer.ai_feedback}</div>
                            )}
                          </div>
                        )}
                        
                        {/* Manual Override */}
                        <div className="mt-3 p-3 bg-gray-100 rounded">
                          <div className="font-semibold text-sm mb-2">✏️ Corrección Manual:</div>
                          {editingAnswer === answer.question_id ? (
                            <div>
                              <div className="mb-2">
                                <label className="block text-sm mb-1">Puntuación (0-100):</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  defaultValue={answer.manual_score || answer.ai_score || 0}
                                  id={`score-${answer.question_id}`}
                                  className="w-full px-2 py-1 border rounded"
                                />
                              </div>
                              <div className="mb-2">
                                <label className="block text-sm mb-1">Feedback:</label>
                                <textarea
                                  defaultValue={answer.manual_feedback || ''}
                                  id={`feedback-${answer.question_id}`}
                                  className="w-full px-2 py-1 border rounded"
                                  rows="2"
                                />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    const score = parseInt(document.getElementById(`score-${answer.question_id}`).value);
                                    const feedback = document.getElementById(`feedback-${answer.question_id}`).value;
                                    handleUpdateScore(selectedResult.id, answer.question_id, score, feedback);
                                  }}
                                  className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                                >
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setEditingAnswer(null)}
                                  className="px-3 py-1 bg-gray-400 text-white rounded text-sm"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {answer.manual_score !== null ? (
                                <div>
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm font-semibold">Puntuación manual:</span>
                                    <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded text-sm font-bold">
                                      {answer.manual_score}/100
                                    </span>
                                  </div>
                                  {answer.manual_feedback && (
                                    <div className="text-sm text-gray-700 mb-2">{answer.manual_feedback}</div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-600 mb-2">Sin corrección manual</div>
                              )}
                              <button
                                onClick={() => setEditingAnswer(answer.question_id)}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                              >
                                {answer.manual_score !== null ? 'Editar' : 'Corregir Manualmente'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-2 text-xs text-gray-500">
                      ⏱️ Tiempo: {answer.time_taken}s | 🎯 Puntos: {question.points}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <div className="text-lg font-bold">
                  Puntuación Total: <span className="text-purple-600">{selectedResult.total_score}</span> / {texthunt.total_points}
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setEditingAnswer(null);
                  }}
                  className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regrade Modal */}
      {showRegradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">🔄 Recalificar con Nuevo Nivel</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Esto re-evaluará TODAS las respuestas cortas con el nuevo nivel de exigencia. 
              Las correcciones manuales del profesor no se sobrescribirán.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-3">Selecciona el nuevo nivel:</label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
                  <input
                    type="radio"
                    name="new_strictness"
                    value="lenient"
                    checked={newStrictness === 'lenient'}
                    onChange={(e) => setNewStrictness(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <span className="font-semibold">😊 Flexible</span>
                    <p className="text-xs text-gray-600">Más tolerante, valora la idea general</p>
                  </div>
                </label>
                <label className="flex items-center cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
                  <input
                    type="radio"
                    name="new_strictness"
                    value="normal"
                    checked={newStrictness === 'normal'}
                    onChange={(e) => setNewStrictness(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <span className="font-semibold">⚖️ Normal</span>
                    <p className="text-xs text-gray-600">Equilibrio entre comprensión y precisión</p>
                  </div>
                </label>
                <label className="flex items-center cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
                  <input
                    type="radio"
                    name="new_strictness"
                    value="strict"
                    checked={newStrictness === 'strict'}
                    onChange={(e) => setNewStrictness(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <span className="font-semibold">🎓 Exigente</span>
                    <p className="text-xs text-gray-600">Requiere respuestas completas y precisas</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowRegradeModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={handleRegrade}
                disabled={regrading}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {regrading ? 'Recalificando...' : 'Recalificar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Classroom Export Modal */}
      {showClassroomModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">📤 Exportar a Google Classroom</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Selecciona la clase</label>
              <select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setSelectedCoursework('');
                  setAvailableCourseworks([]);
                  if (e.target.value) {
                    loadCourseworks(e.target.value);
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Selecciona una clase --</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name} {course.section && `(${course.section})`}
                  </option>
                ))}
              </select>
            </div>

            {selectedCourse && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Opción de Tarea</label>
                  
                  <div className="space-y-3">
                    {/* Crear nueva */}
                    <button
                      onClick={handleCreateClassroomAssignment}
                      disabled={!selectedCourse || creatingAssignment}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    >
                      {creatingAssignment ? 'Creando...' : '✨ Crear Nueva Tarea en Classroom'}
                    </button>
                    
                    {/* Seleccionar existente */}
                    <div className="border-t pt-3">
                      <p className="text-sm font-medium mb-2">O selecciona una tarea existente:</p>
                      {loadingCourseworks ? (
                        <p className="text-sm text-gray-600">Cargando tareas...</p>
                      ) : availableCourseworks.length > 0 ? (
                        <select
                          value={selectedCoursework}
                          onChange={(e) => setSelectedCoursework(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Selecciona una tarea existente --</option>
                          {availableCourseworks.map((cw) => (
                            <option key={cw.id} value={cw.id}>
                              {cw.title} ({cw.maxPoints} pts) - {cw.state}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm text-gray-500">No hay tareas en esta clase</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedCoursework && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">✅ Tarea creada exitosamente</p>
              </div>
            )}

            <div className="border-t pt-4 mb-4">
              <button
                onClick={handleSubmitAllGrades}
                disabled={!selectedCourse || !selectedCoursework || submittingGrades}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingGrades ? 'Enviando...' : `📊 Enviar ${results.length} Calificaciones`}
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowClassroomModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal Editar Calificación */}
      {showEditScoreModal && editingScore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              ✏️ Editar Calificación
            </h2>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                <strong>Estudiante:</strong> {editingScore.student_name}
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Calificación Actual:</strong> <span className="text-purple-600 font-bold">{editingScore.total_score}</span> / {texthunt.total_points}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nueva Calificación (0 - {texthunt.total_points})
              </label>
              <input
                type="number"
                min="0"
                max={texthunt.total_points}
                value={newScore}
                onChange={(e) => setNewScore(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ingresa la nueva calificación"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo de la Modificación <span className="text-red-500">*</span>
              </label>
              <textarea
                value={scoreReason}
                onChange={(e) => setScoreReason(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Penalización por copia, Error en la corrección original, etc."
              />
              <p className="text-xs text-gray-500 mt-1">
                Este motivo quedará registrado en el historial
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEditScoreModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveScore}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                💾 Guardar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default TextHuntResults;
